import { useState, useEffect } from 'react';
import { Calendar, Plus, Search, Eye, Pencil, Trash2, X, Check, ChevronLeft, ChevronRight, UserCheck, Copy, ExternalLink, Clock } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { DailyBrief, Influencer, dailyBriefsRef, influencersRef, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { auth, db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function DailyBriefPage() {
  const { theme } = useTheme();
  const [briefs, setBriefs] = useState<DailyBrief[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState<DailyBrief | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedInfluencer, setSelectedInfluencer] = useState<string>('');
  const [newTask, setNewTask] = useState('');
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<DailyBrief>>({
    agencyName: '',
    influencerId: '',
    influencerName: '',
    influencerSlug: '',
    date: new Date(),
    tasks: [],
    internalNotes: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [briefsData, influencersData] = await Promise.all([
        getDocuments<DailyBrief>(dailyBriefsRef),
        getDocuments<Influencer>(influencersRef),
      ]);
      setBriefs(briefsData);
      setInfluencers(influencersData.filter(inf => inf.isExclusive && !inf.archived));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Aucun utilisateur connecté');

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) throw new Error('Profil utilisateur non trouvé');

      const userData = userDoc.data();
      let agencyName = 'Le Suivi'; // Default name
      
      if (userData.agencyId) {
        const agencyDoc = await getDoc(doc(db, 'agencies', userData.agencyId));
        if (agencyDoc.exists()) {
          agencyName = agencyDoc.data().name;
        }
      }

      const selectedInf = influencers.find(inf => inf.id === formData.influencerId);
      if (!selectedInf) return;

      const briefData = {
        ...formData,
        agencyName,
        influencerName: selectedInf.fullName,
        influencerSlug: generateSlug(selectedInf.fullName),
        updatedAt: new Date(),
      };

      if (selectedBrief?.id) {
        await updateDocument(dailyBriefsRef, selectedBrief.id, briefData);
      } else {
        await createDocument(dailyBriefsRef, {
          ...briefData,
          createdAt: new Date(),
        } as DailyBrief);
      }

      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving brief:', error);
    }
  };

  const handleDelete = async (briefId: string) => {
    try {
      await deleteDocument(dailyBriefsRef, briefId);
      await loadData();
    } catch (error) {
      console.error('Error deleting brief:', error);
    }
  };

  const handleEdit = (brief: DailyBrief) => {
    setSelectedBrief(brief);
    setFormData({
      agencyName: brief.agencyName,
      influencerId: brief.influencerId,
      influencerName: brief.influencerName,
      influencerSlug: brief.influencerSlug,
      date: brief.date,
      tasks: [...brief.tasks],
      internalNotes: brief.internalNotes,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBrief(null);
    setNewTask('');
    setFormData({
      agencyName: '',
      influencerId: '',
      influencerName: '',
      influencerSlug: '',
      date: new Date(),
      tasks: [],
      internalNotes: '',
    });
  };

  const handleAddTask = () => {
    if (newTask.trim()) {
      const task = {
        id: Date.now().toString(),
        text: newTask.trim(),
        completed: false,
      };
      setFormData(prev => ({
        ...prev,
        tasks: [...(prev.tasks || []), task]
      }));
      setNewTask('');
    }
  };

  const handleRemoveTask = (taskId: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks?.filter(task => task.id !== taskId) || []
    }));
  };

  const handleTaskChange = (taskId: string, newText: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks?.map(task => 
        task.id === taskId ? { ...task, text: newText } : task
      ) || []
    }));
  };

  const copyInfluencerLink = async (brief: DailyBrief) => {
    const link = `${window.location.origin}/brief/${brief.influencerSlug}`;
    await navigator.clipboard.writeText(link);
    setCopiedLink(brief.id!);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const formatDate = (date: Date | { seconds: number } | undefined) => {
    if (!date) return 'Date inconnue';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateInput = (date: Date | { seconds: number } | undefined) => {
    if (!date) return '';
    
    if ('seconds' in date) {
      return new Date(date.seconds * 1000).toISOString().split('T')[0];
    }
    
    return new Date(date).toISOString().split('T')[0];
  };

  const isSameDay = (date1: Date | { seconds: number }, date2: Date) => {
    let d1: Date;
    if (typeof date1 === 'string') {
      d1 = new Date(date1);
    } else if (date1 && typeof date1 === 'object' && 'seconds' in date1) {
      d1 = new Date(date1.seconds * 1000);
    } else {
      d1 = date1 as Date;
    }
    return d1.toDateString() === date2.toDateString();
  };

  const getWeekDates = (date: Date) => {
    const monday = new Date(date);
    monday.setDate(monday.getDate() - monday.getDay() + 1);
    
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      return day;
    });
  };

  const previousWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentDate(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentDate(newDate);
  };

  const getBriefsForDate = (date: Date) => {
    return briefs.filter(brief => isSameDay(brief.date, date));
  };

  const filteredInfluencers = influencers.filter(inf =>
    inf.fullName.toLowerCase().includes(search.toLowerCase())
  );

  const weekDates = getWeekDates(currentDate);
  const weekdays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Tâche influenceur</h1>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Gérez les tâches quotidiennes de vos influenceurs exclusifs</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouvelle tâche
        </button>
      </div>

      <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border p-6`}>
        {/* Navigation semaine */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-1`}>
              Planning hebdomadaire des tâches influenceurs
            </h2>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              Semaine du {formatDate(weekDates[0])} au {formatDate(weekDates[6])}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={previousWeek}
              className={`p-2 ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-dark-200' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-colors`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextWeek}
              className={`p-2 ${theme === 'dark' ? 'text-gray-400 hover:text-white hover:bg-dark-200' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} rounded-lg transition-colors`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-4">
            {weekDates.map((date, index) => (
              <div key={index} className={`${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'} rounded-lg`}>
                <div className={`p-3 border-b ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'}`}>
                  <h3 className={`${theme === 'dark' ? 'text-white' : 'text-gray-900'} font-medium`}>{weekdays[index]}</h3>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{formatDate(date)}</p>
                </div>
                <div className="p-3 space-y-2 min-h-[200px]">
                  {getBriefsForDate(date).map((brief) => (
                    <div
                      key={brief.id} 
                      className={`group relative ${theme === 'dark' ? 'bg-dark-200 hover:bg-dark-300' : 'bg-white hover:bg-gray-50'} rounded-lg p-3 cursor-pointer transition-all border ${theme === 'dark' ? 'border-dark-300 hover:border-accent-blue/30' : 'border-gray-200 hover:border-accent-blue/30'} hover:shadow-sm`}
                    >
                      {/* Header avec nom de l'influenceur */}
                      <div className={`flex items-center justify-between mb-3 pb-2 border-b ${theme === 'dark' ? 'border-dark-300' : 'border-gray-200'}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className={`w-7 h-7 rounded-full ${theme === 'dark' ? 'bg-accent-blue/20' : 'bg-accent-blue/10'} flex items-center justify-center flex-shrink-0`}>
                            <span className="text-xs font-bold text-accent-blue">
                              {brief.influencerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                              {brief.influencerName}
                            </h4>
                            <div className="flex items-center gap-1 mt-0.5">
                              <Clock className={`w-3 h-3 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`} />
                              <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                                {brief.tasks.filter(t => t.completed).length}/{brief.tasks.length} terminées
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions en haut à droite */}
                      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyInfluencerLink(brief);
                          }}
                          className={`p-1.5 ${theme === 'dark' ? 'bg-dark-400/80 text-gray-300 hover:text-white hover:bg-dark-400' : 'bg-white/80 text-gray-600 hover:text-gray-900 hover:bg-white'} rounded-md transition-all backdrop-blur-sm shadow-sm`}
                          title="Copier le lien influenceur"
                        >
                          {copiedLink === brief.id ? (
                            <Check className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </button>
                        <a
                          href={`/brief/${brief.influencerSlug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className={`p-1.5 ${theme === 'dark' ? 'bg-dark-400/80 text-gray-300 hover:text-white hover:bg-dark-400' : 'bg-white/80 text-gray-600 hover:text-gray-900 hover:bg-white'} rounded-md transition-all backdrop-blur-sm shadow-sm`}
                          title="Voir la vue influenceur"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </a>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(brief);
                          }}
                          className={`p-1.5 ${theme === 'dark' ? 'bg-dark-400/80 text-gray-300 hover:text-white hover:bg-dark-400' : 'bg-white/80 text-gray-600 hover:text-gray-900 hover:bg-white'} rounded-md transition-all backdrop-blur-sm shadow-sm`}
                          title="Modifier"
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(brief.id!);
                          }}
                          className={`p-1.5 ${theme === 'dark' ? 'bg-red-500/20 text-red-400 hover:text-red-300 hover:bg-red-500/30' : 'bg-red-50 text-red-600 hover:text-red-700 hover:bg-red-100'} rounded-md transition-all backdrop-blur-sm shadow-sm`}
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                      
                      {/* Liste des tâches */}
                      <div className="space-y-1">
                        {brief.tasks.slice(0, 3).map((task) => (
                          <div key={task.id} className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${task.completed ? 'bg-green-500' : theme === 'dark' ? 'bg-gray-500' : 'bg-gray-400'}`} />
                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} truncate ${task.completed ? 'line-through opacity-60' : ''}`}>
                              {task.text}
                            </span>
                          </div>
                        ))}
                        {brief.tasks.length > 3 && (
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'} pl-4`}>
                            +{brief.tasks.length - 3} tâche(s)
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border-2 border-black">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-gray-600 hover:text-gray-800"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {selectedBrief ? 'Modifier la tâche' : 'Nouvelle tâche influenceur'}
                </h2>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Influenceur exclusif
                      </label>
                      <select
                        value={formData.influencerId}
                        onChange={(e) => setFormData({ ...formData, influencerId: e.target.value })}
                        className="mt-1 block w-full border-2 border-black"
                        required
                      >
                        <option value="">Sélectionner un influenceur</option>
                        {influencers.map((influencer) => (
                          <option key={influencer.id} value={influencer.id}>
                            {influencer.fullName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Date
                      </label>
                      <input
                        type="date"
                        value={formatDateInput(formData.date)}
                        onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value) })}
                        className="mt-1 block w-full border-2 border-black"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tâches
                    </label>
                    <div className="space-y-3">
                      {formData.tasks?.map((task, index) => (
                        <div key={task.id} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={task.text}
                            onChange={(e) => handleTaskChange(task.id, e.target.value)}
                            className="flex-1 border-2 border-black"
                            placeholder="Description de la tâche"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(task.id)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newTask}
                          onChange={(e) => setNewTask(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddTask();
                            }
                          }}
                          className="flex-1 border-2 border-black"
                          placeholder="Nouvelle tâche..."
                        />
                        <button
                          type="button"
                          onClick={handleAddTask}
                          className="px-3 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 border-2 border-black"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Notes internes (cachées pour l'influenceur)
                    </label>
                    <textarea
                      value={formData.internalNotes}
                      onChange={(e) => setFormData({ ...formData, internalNotes: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-2 border-black"
                      placeholder="Notes visibles uniquement par l'équipe..."
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-black rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-accent-blue border-2 border-black rounded-lg hover:bg-accent-blue/90"
                  >
                    {selectedBrief ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>
    </div>
  );
}